import React, { useContext, useState } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { nanoid } from "nanoid";
import { CircleX, Upload } from "lucide-react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Cross2Icon } from "@radix-ui/react-icons";
import baseUrl from "../../config/baseUrl";
import { showSuccess, showError } from "../../utils/toast";

const MultiFileUpload = ({ setShowUploadButton, documentSourceType }) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const { token } = useContext(AuthContext);
  const [hasLargeFile, setHasLargeFile] = useState(false);
  const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB

  const MAX_FILE_SIZE = 300 * 1024 * 1024; // 300MB

  const allowedExtensionsRegex =
    /\.(jpg|jpeg|jfif|gif|png|bmp|pdf|xlsx|xls|zip|doc|docx|rar|ppt|pptx|txt|rtf|mp4|mov|wmv|mp3|aac|amr|opus|tiff|tif|webp|heic|heif|ico|csv|html|htm|bin|one|m4a|eml|ics|mhtml|pages|url|vcf|3gp|msg|crdownload|wav|aiff|aif|flac|alac|ogg|oga|wma|midi|mid|mp1|mp2|mpa|mpeg|mpg)$/i;

  // Exact extension list mirrored from the regex for dropzone's file chooser
  const ACCEPT_EXTENSIONS = [
    ".jpg",
    ".jpeg",
    ".jfif",
    ".gif",
    ".png",
    ".bmp",
    ".pdf",
    ".xlsx",
    ".xls",
    ".zip",
    ".doc",
    ".docx",
    ".rar",
    ".ppt",
    ".pptx",
    ".txt",
    ".rtf",
    ".mp4",
    ".mov",
    ".wmv",
    ".mp3",
    ".aac",
    ".amr",
    ".opus",
    ".tiff",
    ".tif",
    ".webp",
    ".heic",
    ".heif",
    ".ico",
    ".csv",
    ".html",
    ".htm",
    ".bin",
    ".one",
    ".m4a",
    ".eml",
    ".ics",
    ".mhtml",
    ".pages",
    ".url",
    ".vcf",
    ".3gp",
    ".msg",
    ".crdownload",
    // newly added audio
    ".wav",
    ".aiff",
    ".aif",
    ".flac",
    ".alac",
    ".ogg",
    ".oga",
    ".wma",
    ".midi",
    ".mid",
    ".mp1",
    ".mp2",
    ".mpa",
    ".mpeg",
    ".mpg",
  ];

  const onDrop = (acceptedFiles) => {
    const validFiles = [];
    let containsLargeFile = false;

    acceptedFiles.forEach((file) => {
      const isValidExtension = allowedExtensionsRegex.test(file.name);
      const isValidSize = file.size <= MAX_FILE_SIZE;

      if (!isValidExtension) {
        showError(`${file.name} is not a supported file format`);
        return;
      }

      if (!isValidSize) {
        showError(`${file.name} exceeds 300MB limit`);
        return;
      }

      if (file.size > COMPRESSION_THRESHOLD) {
        containsLargeFile = true;
      }

      validFiles.push(file);
    });

    setHasLargeFile(containsLargeFile);

    const newFiles = validFiles.map((file) => ({
      id: nanoid(),
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: ACCEPT_EXTENSIONS.join(","),
  });

  const handleDelete = (fileId) => {
    setFiles(files.filter((file) => file.id !== fileId));
  };

  const uploadFile = async (file) => {
    const formData = new FormData();

    formData.append("document_source_type", documentSourceType);
    formData.append("documents[]", file.file);

    try {
      const response = await axios.post(
        `${baseUrl}/api/upload-multiple-mailroom-document`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          onUploadProgress: (progressEvent) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [file.id]: progress,
            }));
          },
        },
      );
    } catch (error) {
      console.error("Upload error:", error);

      if (error.response?.data?.error) {
        showError(`${file.file.name}: ${error.response.data.error}`);
      } else {
        showError(`${file.file.name}: Upload failed`);
      }
    }
  };

  const uploadAllFiles = async () => {
    setIsUploading(true);
    for (const file of files) {
      await uploadFile(file);
    }
    setIsUploading(false);
    setShowUploadButton(false);
  };
  {
    hasLargeFile && (
      <div className="w-full mt-4 p-3 bg-yellow-100 text-yellow-800 rounded-md text-center font-medium">
        ⚠️ Large file detected (greater than 5MB). Upload may take longer due to
        compression. Please wait...
      </div>
    );
  }
  return (
    <div className="h-auto overflow-y-auto w-[65%] my-0 mx-auto flex flex-col items-center bg-white shadow-md rounded-md mt-6 p-12 relative">
      <Cross2Icon
        className=" absolute top-2 right-1 bg-[#1a6b7c] hover:bg-[#155e6e] rounded-full text-white size-6 p-1 cursor-pointer transition-colors"
        onClick={() => setShowUploadButton(false)}
      />
      <div
        {...getRootProps({ className: "dropzone" })}
        className="border-dashed border-2 border-gray-400 p-4 w-full text-center cursor-pointer h-[200px] rounded-md flex flex-col items-center justify-center"
      >
        <input {...getInputProps()} />
        <div className="flex flex-col justify-center items-center">
          <Upload />
          <p className="font-bold text-muted-foreground text-lg">
            Drop a file here or click to upload
          </p>
          <small className="text-base ">Maximum upload size: 300 MB</small>
        </div>
      </div>
      {/* ✅ MOVE IT HERE */}
      {hasLargeFile && (
        <div className="w-full mt-4 p-3 bg-yellow-100 text-yellow-800 rounded-md text-center font-medium">
          ⚠️ Large file detected (greater than 5MB). Upload may take longer due
          to compression. Please wait...
        </div>
      )}
      <div className="w-full mt-4">
        {files.map((fileWrapper) => (
          <div
            key={fileWrapper.id}
            className="flex items-center justify-between gap-2 p-2 border-gray-300 shadow-md rounded-md mt-2"
          >
            <div className="flex flex-1 items-center">
              <div className="w-full">
                <p>{fileWrapper.file.name}</p>
                <progress
                  value={uploadProgress[fileWrapper.id] || 0}
                  max="100"
                  className="w-full [&::-webkit-progress-bar]:rounded-lg [&::-webkit-progress-value]:rounded-lg   [&::-webkit-progress-bar]:bg-slate-100 [&::-webkit-progress-value]:bg-green-300 [&::-moz-progress-bar]:bg-green-300"
                />
              </div>
            </div>

            <CircleX
              onClick={() => handleDelete(fileWrapper.id)}
              className="text-[#1a6b7c] hover:text-[#155e6e] cursor-pointer transition-colors"
            />
          </div>
        ))}
      </div>
      {files.length > 0 && (
        <button
          className="bg-[#1a6b7c] hover:bg-[#155e6e] active:bg-[#155e6e] text-white px-4 py-2 rounded mt-4 flex items-center gap-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          onClick={uploadAllFiles}
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Upload
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default MultiFileUpload;
